import { CommonModule } from "@angular/common";
import { Component, computed, DestroyRef, effect, inject, signal } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslocoService } from "@jsverse/transloco";
import { ClerkService } from "ngx-clerk-iliad";
import { posthog } from "posthog-js";
import { MenuItem } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { MenuModule } from "primeng/menu";
import { RippleModule } from "primeng/ripple";
import { SelectButtonModule } from "primeng/selectbutton";
import { take } from "rxjs";
import { LANGUAGE_METADATA_LOCALIZED, LanguageCode } from "../../../../domain/languages";
import { isAdminRouteData } from "../../../../domain/route-data";
import { EmailNotificationFrequencyKind, formatDailyEmailSendTime } from "../../../../domain/userSettings";
import { ConnectDialogService } from "../../../../services/connect-dialog.service";
import { LocalizationService } from "../../../../services/localization.service";
import { NavigationService } from "../../../../services/navigation.service";
import { OrganizationService } from "../../../../services/organization.service";
import { ToastService } from "../../../../services/toast.service";
import { UserService } from "../../../../services/user.service";
import { localeForLang } from "../../../pipes/locale";
import { LayoutService } from "../../apollo-template/service/layout.service";

@Component({
  selector: "app-profile-menu",
  standalone: true,
  imports: [ButtonModule, SelectButtonModule, MenuModule, FormsModule, CommonModule, RippleModule],
  templateUrl: "./profile-menu.component.html",
})
export class AppProfileMenuComponent {
  private readonly _translocoService = inject(TranslocoService);
  private readonly _userService = inject(UserService);
  private readonly _clerkService = inject(ClerkService);
  private readonly _router = inject(Router);
  private readonly _layoutService = inject(LayoutService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _connectDialogService = inject(ConnectDialogService);
  private readonly _route = inject(ActivatedRoute);
  private readonly _localizationService = inject(LocalizationService);
  private readonly _navigationService = inject(NavigationService);
  private readonly _organizationService = inject(OrganizationService);
  private readonly _toastService = inject(ToastService);

  public $user = toSignal(this._userService.getSelf$());
  public $activeOrganization = toSignal(this._organizationService.activeOrganization$);
  public $darkTheme = computed(() => this._layoutService.layoutConfig().darkTheme);
  public $remoteThemePreference = computed(() => this.$user()?.settings?.themePreference || "light");
  public $isAdminArea = signal(false);
  // Set on selection so the check mark moves immediately; cleared only if the save fails. On success it matches the
  // reloaded user, so it is left in place.
  private readonly $pendingFrequency = signal<EmailNotificationFrequencyKind | null>(null);
  public $emailNotificationFrequency = computed<EmailNotificationFrequencyKind>(
    () =>
      this.$pendingFrequency() ??
      this.$user()?.settings?.emailNotificationFrequency ??
      EmailNotificationFrequencyKind.Asap,
  );

  themeOptions = [
    { name: this._translocoService.translate("app.common.lightMode"), value: false },
    { name: this._translocoService.translate("app.common.darkMode"), value: true },
  ];

  // All system languages will be displayed in the menu automatically
  availableLanguages = Object.entries(LANGUAGE_METADATA_LOCALIZED).map(([languageCode, languageMetadata]) => ({
    code: languageCode as LanguageCode,
    name: languageMetadata.name,
  }));

  languageSelectorExpanded = false;

  /**
   * t(app.profileMenu.emailNotificationFrequency.asap, app.profileMenu.emailNotificationFrequency.hourly)
   * t(app.profileMenu.emailNotificationFrequency.every4Hours, app.profileMenu.emailNotificationFrequency.daily)
   * t(app.profileMenu.emailNotificationFrequency.off)
   */
  emailNotificationOptions = Object.values(EmailNotificationFrequencyKind).map((frequency) => ({
    value: frequency,
    // Only `daily` uses {{time}}; the other labels ignore it.
    label: this._translocoService.translate(`app.profileMenu.emailNotificationFrequency.${frequency}`, {
      time: formatDailyEmailSendTime(localeForLang(this._translocoService.getActiveLang())),
    }),
  }));

  emailNotificationSelectorExpanded = false;

  menuItems = computed<MenuItem[]>(() => {
    const isAdmin = this.$user()?.connectRole === "Admin";
    const activeOrganizationId = this.$activeOrganization()?.id;
    const isAdminArea = this.$isAdminArea();

    return [
      {
        label: this._translocoService.translate("app.profileMenu.adminTools"),
        icon: "pi pi-cog",
        visible: isAdmin && !isAdminArea,
        command: () => {
          this._navigationService.navigateToAdminHome();
        },
      },
      {
        label: this._translocoService.translate("app.profileMenu.adminToolsExit"),
        icon: "pi pi-cog",
        styleClass:
          "text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/15 transition-colors duration-200",
        visible: isAdmin && isAdminArea,
        command: () => {
          this._navigationService.navigateAwayFromAdminTools(activeOrganizationId);
        },
      },
      {
        label: this._translocoService.translate("app.profileMenu.clerkProfileButton"),
        icon: "pi pi-unlock",
        command: () => {
          posthog.capture("clerk_profile_opened");

          this._userService
            .getSelf$()
            .pipe(take(1))
            .subscribe((user) => {
              this._userService.openClerkUserProfile(user);
            });
        },
      },
      {
        label: this._translocoService.translate("app.profileMenu.language"),
        icon: "pi pi-language",
        id: "language-selector",
      },
      {
        label: this._translocoService.translate("app.profileMenu.emailNotifications"),
        icon: "pi pi-bell",
        id: "notification-frequency-selector",
      },
      {
        label: this._translocoService.translate("app.profileMenu.tos"),
        icon: "pi pi-file-check",
        command: () => {
          this._connectDialogService.displayOrganizationTosDialog(
            this._route.snapshot.data["organization"],
            false,
            true,
          );
        },
      },
      {
        label: this._translocoService.translate("app.profileMenu.signOut"),
        icon: "pi pi-sign-out",
        command: () => {
          this._router.navigate(["/signOut"]);
        },
      },
    ];
  });

  constructor() {
    this._route.data.pipe(takeUntilDestroyed()).subscribe((data) => {
      const adminData = data["admin"];
      if (isAdminRouteData(adminData)) {
        this.$isAdminArea.set(adminData.isAdminArea);
      } else {
        this.$isAdminArea.set(false);
      }
    });

    effect(() => {
      // Watch remote user settings, and if they change (on sign-in for example), update locally
      const remotePref = this.$remoteThemePreference();
      const isRemoteDarkTheme = remotePref === "dark";
      if (isRemoteDarkTheme !== this.$darkTheme()) {
        this._layoutService.layoutConfig.update((state) => ({
          ...state,
          darkTheme: isRemoteDarkTheme,
        }));
      }
    });
  }

  // Triggered by UI interaction.
  public toggleDarkMode(): void {
    this._layoutService.layoutConfig.update((state) => {
      const newTheme = !state.darkTheme ? "dark" : "light";
      this._userService
        .updateSettings({
          themePreference: newTheme,
        })
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe();
      return {
        ...state,
        darkTheme: !state.darkTheme,
      };
    });
  }

  public selectLanguage(languageCode: LanguageCode): void {
    this._localizationService.changeLanguage(languageCode);
    this.languageSelectorExpanded = false;
  }

  public toggleLanguageSelector(event: Event): void {
    // Prevent profile menu from closing when clicking on the language selector
    event.stopPropagation();
    this.languageSelectorExpanded = !this.languageSelectorExpanded;
  }

  public toggleEmailNotificationSelector(event: Event): void {
    // Prevent profile menu from closing when clicking on the selector
    event.stopPropagation();
    this.emailNotificationSelectorExpanded = !this.emailNotificationSelectorExpanded;
  }

  public selectEmailNotificationFrequency(frequency: EmailNotificationFrequencyKind, event: Event): void {
    // Keep the menu and the panel open; the selection shows immediately via the pending value.
    event.stopPropagation();
    this.$pendingFrequency.set(frequency);
    this._userService
      .updateSettings({ emailNotificationFrequency: frequency })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        error: () => {
          // Only roll back if a newer selection hasn't replaced this one.
          if (this.$pendingFrequency() === frequency) {
            this.$pendingFrequency.set(null);
          }
          this._toastService.error(this._translocoService.translate("app.common.errors.saveError"));
        },
      });
  }
}
